import { runQuery } from './bqClient';
import type {
  AggregatedLocationDatum,
  LocationDatum,
  Segment,
  ObjectivityRecord,
  TopicTensionRecord,
  CompassRecord,
  TitlesRecord,
  WordCloudEntry,
  WeekKpiTotals,
  VipHypeRecord,
  VipTopicRecord
} from '../types';

export async function fetchWeeks(): Promise<string[]> {
  const rows = await runQuery<{ week_start_date: string }>({
    query: `
      SELECT DISTINCT week_start_date
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_1_users\`
      ORDER BY week_start_date DESC
    `
  });
  return rows.map((r) => r.week_start_date);
}

export async function fetchLocations(week: string): Promise<string[]> {
  const rows = await runQuery<{ user_location: string }>({
    query: `
      SELECT DISTINCT user_location
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_2_topics\`
      WHERE week_start_date = @week
      ORDER BY user_location
    `,
    params: { week }
  });
  const list = rows.map((r) => r.user_location).filter(Boolean);
  return ['All', ...list];
}

export async function fetchWeekKpis(week: string): Promise<WeekKpiTotals> {
  const rows = await runQuery<{ user_segment: Segment; users: number }>({
    query: `
      SELECT user_segment, COUNT(DISTINCT user_id) AS users
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_1_users\`
      WHERE week_start_date = @week
        AND content_category = 'Political'
      GROUP BY user_segment
    `,
    params: { week }
  });

  const totals: WeekKpiTotals = {
    politicsUsers: 0,
    leftUsers: 0,
    neutralUsers: 0,
    rightUsers: 0
  };

  rows.forEach((row) => {
    const value = Number(row.users ?? 0);
    if (row.user_segment === 'Left') totals.leftUsers = value;
    if (row.user_segment === 'Neutral') totals.neutralUsers = value;
    if (row.user_segment === 'Right') totals.rightUsers = value;
    totals.politicsUsers += value;
  });

  return totals;
}

export async function fetchTopLocations(category: 'Political' | 'General', week: string) {
  const segments: Segment[] = ['Left', 'Neutral', 'Right'];
  const singles: Record<Segment, LocationDatum[]> = { Left: [], Neutral: [], Right: [] };

  for (const segment of segments) {
    const rows = await runQuery<{ user_location: string; active_users: number }>({
      query: `
        SELECT user_location, COUNT(DISTINCT user_id) AS active_users
        FROM \`ocm-ml-dashboard.political_polarity_cy.export_1_users\`
        WHERE week_start_date = @week
          AND content_category = @category
          AND user_segment = @segment
        GROUP BY 1
        ORDER BY 2 DESC
        LIMIT 15
      `,
      params: { week, category, segment }
    });
    singles[segment] = rows.map((r) => ({ location: r.user_location, value: Number(r.active_users) }));
  }

  const stackedRows = await runQuery<{
    user_location: string;
    left_users: number;
    right_users: number;
    neutral_users: number;
    total_users: number;
  }>({
    query: `
      SELECT
        user_location,
        SUM(CASE WHEN user_segment = 'Left' THEN 1 ELSE 0 END) AS left_users,
        SUM(CASE WHEN user_segment = 'Right' THEN 1 ELSE 0 END) AS right_users,
        SUM(CASE WHEN user_segment = 'Neutral' THEN 1 ELSE 0 END) AS neutral_users,
        COUNT(DISTINCT user_id) AS total_users
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_1_users\`
      WHERE week_start_date = @week
        AND content_category = @category
      GROUP BY 1
      ORDER BY total_users DESC
      LIMIT 15
    `,
    params: { week, category }
  });

  const stacks: AggregatedLocationDatum[] = stackedRows.map((r) => ({
    location: r.user_location,
    left: Number(r.left_users),
    right: Number(r.right_users),
    neutral: Number(r.neutral_users),
    total: Number(r.total_users)
  }));

  return { singles, stacks };
}

export async function fetchTopicsForLocation(
  location: string,
  category: 'Political' | 'General',
  segment: Segment,
  week: string
): Promise<TitlesRecord[]> {
  const rows = await runQuery<{
    topic_label: string;
    total_engagement: number;
    representative_title: string;
    segment_engagement: number;
    title_left?: string;
    title_right?: string;
    title_neutral?: string;
    display_title: string;
  }>({
    query: `
      WITH base AS (
        SELECT
          topic_label,
          SUM(total_engagement) as total_engagement,
          MAX(representative_title) as representative_title,
          MAX(title_left) as title_left,
          MAX(title_right) as title_right,
          MAX(title_neutral) as title_neutral,
          MAX(CASE
            WHEN @segment = 'Left' THEN COALESCE(title_left, representative_title)
            WHEN @segment = 'Right' THEN COALESCE(title_right, representative_title)
            WHEN @segment = 'Neutral' THEN COALESCE(title_neutral, representative_title)
            ELSE representative_title
          END) AS display_title,
          SUM(CASE
            WHEN @segment = 'Left' THEN views_left
            WHEN @segment = 'Right' THEN views_right
            ELSE views_neutral
          END) AS segment_engagement
        FROM \`ocm-ml-dashboard.political_polarity_cy.export_2_topics\`
        WHERE week_start_date = @week
          AND (@location = 'All' OR user_location = @location)
          AND content_category = @category
        GROUP BY topic_label
      ),
      diaspora AS (
        SELECT
          topic_label,
          SUM(total_engagement) as total_engagement,
          MAX(representative_title) as representative_title,
          MAX(title_left) as title_left,
          MAX(title_right) as title_right,
          MAX(title_neutral) as title_neutral,
          MAX(representative_title) AS display_title,
          SUM(CASE
            WHEN @segment = 'Left' THEN views_left
            WHEN @segment = 'Right' THEN views_right
            ELSE GREATEST(total_views - views_left - views_right, 0)
          END) AS segment_engagement
        FROM \`ocm-ml-dashboard.political_polarity_cy.export_6_diaspora_topics\`
        WHERE week_start_date = @week
          AND (@location = 'All' OR user_location = @location)
          AND content_category = @category
        GROUP BY topic_label
      )
      SELECT topic_label, total_engagement, representative_title, title_left, title_right, title_neutral, display_title, segment_engagement
      FROM base
      UNION ALL
      SELECT topic_label, total_engagement, representative_title, title_left, title_right, title_neutral, display_title, segment_engagement
      FROM diaspora
      ORDER BY segment_engagement DESC, total_engagement DESC
      LIMIT 10
    `,
    params: { week, location, category, segment }
  });

  return rows.map((r, idx) => ({
    id: `${location}-${idx}`,
    location,
    title: sanitizeTitle(r),
    topic: r.topic_label,
    clicks: 0,
    avgEngagedTime: 0,
    avgScroll: 0,
    engagementScore: Number(r.segment_engagement ?? r.total_engagement),
    segmentEngagement: Number(r.segment_engagement ?? 0),
    totalEngagement: Number(r.total_engagement ?? 0)
  }));
}

const sanitizeTitle = (row: {
  display_title?: string;
  representative_title?: string;
  topic_label: string;
}) => {
  const candidates = [
    row.display_title,
    row.representative_title,
    row.topic_label
  ].filter(Boolean) as string[];
  const nonUrl = candidates.find((title) => !/^https?:\/\//i.test(title));
  return nonUrl ?? candidates[0] ?? '';
};

export async function fetchTopicTension(category: 'Political' | 'General', week: string): Promise<TopicTensionRecord[]> {
  const rows = await runQuery<{
    topic_label: string;
    views_left: number;
    views_right: number;
    views_neutral: number;
    total_engagement: number;
  }>({
    query: `
      SELECT
        topic_label,
        SUM(views_left) as views_left,
        SUM(views_right) as views_right,
        SUM(views_neutral) as views_neutral,
        SUM(total_engagement) as total_engagement
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_2_topics\`
      WHERE week_start_date = @week
        AND content_category = @category
      GROUP BY topic_label
    `,
    params: { week, category }
  });

  return rows.map((r) => ({
    topic: r.topic_label,
    left: Number(r.views_left),
    right: Number(r.views_right),
    neutral: Number(r.views_neutral),
    total: Number(r.total_engagement)
  }));
}

export async function fetchVipData(
  category: 'Political' | 'General',
  week: string
): Promise<{
  hype: Record<Segment, VipHypeRecord[]>;
  topics: Record<Segment, Record<string, VipTopicRecord[]>>;
}> {
  const rows = await runQuery<{
    vip_name: string;
    user_segment: Segment;
    topic_label: string;
    engagement: number;
    representative_title: string;
    total_hype: number;
  }>({
    query: `
      SELECT * FROM (
        SELECT 
          vip_name,
          user_segment,
          topic_label,
          engagement_by_vip_readers as engagement,
          representative_title,
          SUM(engagement_by_vip_readers) OVER(PARTITION BY vip_name, user_segment) as total_hype,
          ROW_NUMBER() OVER(PARTITION BY vip_name, user_segment ORDER BY engagement_by_vip_readers DESC) as rank
        FROM \`ocm-ml-dashboard.political_polarity_cy.export_3_vip_cohort\`
        WHERE week_start_date = @week
          AND content_category = @category
      )
      WHERE rank <= 5
      ORDER BY total_hype DESC, engagement DESC
    `,
    params: { week, category }
  });

  const hype: Record<Segment, Record<string, number>> = {
    Left: {},
    Neutral: {},
    Right: {}
  };
  const topics: Record<Segment, Record<string, VipTopicRecord[]>> = {
    Left: {},
    Neutral: {},
    Right: {}
  };

  rows.forEach((r) => {
    const segment = r.user_segment;
    const vip = r.vip_name;
    
    if (!hype[segment]) hype[segment] = {};
    if (!hype[segment][vip]) {
      hype[segment][vip] = Number(r.total_hype);
    }

    if (!topics[segment]) topics[segment] = {};
    if (!topics[segment][vip]) topics[segment][vip] = [];
    
    topics[segment][vip].push({
      topic: r.topic_label || r.representative_title,
      engagement: Number(r.engagement),
      representativeTitle: r.representative_title
    });
  });

  const hypeResult: Record<Segment, VipHypeRecord[]> = {
    Left: [],
    Neutral: [],
    Right: []
  };

  (['Left', 'Neutral', 'Right'] as Segment[]).forEach((seg) => {
    hypeResult[seg] = Object.entries(hype[seg] || {}).map(([vipName, totalHype]) => ({
      vipName,
      segment: seg,
      totalHype
    })).sort((a, b) => b.totalHype - a.totalHype);
  });

  return { hype: hypeResult, topics };
}

export interface MapTopic {
  location: string;
  topics: string[];
  engagement: number;
}

export async function fetchDiasporaTopics(
  category: 'Political' | 'General',
  week: string,
  segment: Segment
): Promise<MapTopic[]> {
  const rows = await runQuery<{
    user_location: string;
    topic_label: string;
    total_engagement: number;
    segment_views: number;
  }>({
    query: `
      SELECT user_location, topic_label, total_engagement, segment_views
      FROM (
          SELECT 
              user_location,
              topic_label,
              total_engagement,
              CASE
                WHEN @segment = 'Left' THEN views_left
                WHEN @segment = 'Right' THEN views_right
                ELSE GREATEST(total_views - views_left - views_right, 0)
              END AS segment_views,
              ROW_NUMBER() OVER(
                PARTITION BY user_location
                ORDER BY
                  CASE
                    WHEN @segment = 'Left' THEN views_left
                    WHEN @segment = 'Right' THEN views_right
                    ELSE GREATEST(total_views - views_left - views_right, 0)
                  END DESC,
                  total_engagement DESC
              ) as rank
          FROM \`ocm-ml-dashboard.political_polarity_cy.export_6_diaspora_topics\`
          WHERE week_start_date = @week
            AND content_category = @category
      )
      WHERE rank <= 3
    `,
    params: { week, category, segment }
  });

  const grouped: Record<string, MapTopic> = {};
  rows.forEach((row) => {
    if (!grouped[row.user_location]) {
      grouped[row.user_location] = { location: row.user_location, topics: [], engagement: 0 };
    }
    grouped[row.user_location].topics.push(row.topic_label);
    grouped[row.user_location].engagement += Number(row.segment_views ?? row.total_engagement ?? 0);
  });

  return Object.values(grouped);
}

export async function fetchMapTopics(
  category: 'Political' | 'General',
  week: string,
  segment: Segment
): Promise<MapTopic[]> {
  const rows = await runQuery<{
    user_location: string;
    topic_label: string;
    representative_title: string;
    total_engagement: number;
    segment_views: number;
  }>({
    query: `
      SELECT user_location, topic_label, representative_title, total_engagement, segment_views
      FROM (
          SELECT 
              user_location,
              topic_label,
              representative_title,
              total_engagement,
              CASE
                WHEN @segment = 'Left' THEN views_left
                WHEN @segment = 'Right' THEN views_right
                ELSE views_neutral
              END AS segment_views,
              ROW_NUMBER() OVER(
                PARTITION BY user_location
                ORDER BY
                  CASE
                    WHEN @segment = 'Left' THEN views_left
                    WHEN @segment = 'Right' THEN views_right
                    ELSE views_neutral
                  END DESC,
                  total_engagement DESC
              ) as rank
          FROM \`ocm-ml-dashboard.political_polarity_cy.export_2_topics\`
          WHERE week_start_date = @week
            AND content_category = @category
      )
      WHERE rank <= 3
    `,
    params: { week, category, segment }
  });

  const grouped: Record<string, MapTopic> = {};
  rows.forEach((row) => {
    if (!grouped[row.user_location]) {
      grouped[row.user_location] = { location: row.user_location, topics: [], engagement: 0 };
    }
    grouped[row.user_location].topics.push(row.topic_label);
    grouped[row.user_location].engagement += Number(row.segment_views ?? row.total_engagement ?? 0);
  });

  return Object.values(grouped);
}

export async function fetchSentiment(
  category: 'Political' | 'General',
  week: string
): Promise<ObjectivityRecord[]> {
  const rows = await runQuery<{
    user_location: string;
    user_segment: Segment;
    avg_objectivity: number;
    views_tone_opinion: number;
    views_tone_fact: number;
    views_tone_mixed: number;
  }>({
    query: `
      SELECT
        user_location,
        user_segment,
        AVG(avg_objectivity) AS avg_objectivity,
        SUM(views_tone_opinion) AS views_tone_opinion,
        SUM(views_tone_fact) AS views_tone_fact,
        SUM(views_tone_mixed) AS views_tone_mixed
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_4_content_quality\`
      WHERE week_start_date = @week
        AND content_category = @category
      GROUP BY user_location, user_segment
      ORDER BY user_location, user_segment
    `,
    params: { week, category }
  });

  return rows.map((r) => {
    const opinion = Number(r.views_tone_opinion ?? 0);
    const fact = Number(r.views_tone_fact ?? 0);
    const mixed = Number(r.views_tone_mixed ?? 0);
    const total = opinion + fact + mixed || 1;
    return {
      segment: r.user_segment,
      avgObjectivity: Number(r.avg_objectivity ?? 0),
      opinion,
      fact,
      mixed,
      total,
      location: r.user_location
    };
  });
}

export async function fetchCompass(week: string, location: string): Promise<CompassRecord[]> {
  const rows = await runQuery<{
    x_polarity: number;
    y_objectivity: number;
    user_count: number;
  }>({
    query: `
      SELECT x_polarity, y_objectivity, SUM(user_count) as user_count
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_5_compass_density\`
      WHERE week_start_date = @week
        AND (@location = 'All' OR user_location = @location)
        AND user_count > 10
        AND ABS(x_polarity) <= 1
        AND y_objectivity <= 1
      GROUP BY x_polarity, y_objectivity
    `,
    params: { week, location }
  });

  return rows.map((r) => ({
    x: Number(r.x_polarity ?? 0),
    y: Number(r.y_objectivity ?? 0),
    count: Number(r.user_count ?? 0)
  }));
}

export async function fetchWordCloud(
  category: 'Political' | 'General',
  week: string,
  location: string,
  segment: Segment
): Promise<WordCloudEntry[]> {
  const rows = await runQuery<{
    term: string;
    score: number;
  }>({
    query: `
      SELECT
        keyword AS term,
        SUM(keyword_weight) AS score
      FROM \`ocm-ml-dashboard.political_polarity_cy.export_7_wordcloud\`
      WHERE week_start_date = @week
        AND content_category = @category
        AND (@location = 'All' OR user_location = @location)
        AND user_segment = @segment
      GROUP BY term
      ORDER BY score DESC
      LIMIT 80
    `,
    params: { week, category, location, segment }
  });

  return rows.map((r) => ({
    term: r.term,
    score: Number(r.score ?? 0)
  }));
}
